import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CreditCard, FileText, ExternalLink, Check, X, Smartphone, Download } from "lucide-react";
import { toast } from "sonner";
import { api, API, eur, fmtDate, errMsg, stepIndex } from "@/lib/api";
import { useT } from "@/i18n";
import { Shell } from "@/components/Shell";
import { StatusBadge, Steps, EventFeed, Photo } from "@/components/ui-craft";
import { QuoteTable } from "@/components/QuoteTable";
import { ListenButton } from "@/components/Ai";

const quoteStatusLabel = (quote, t) => {
  if (quote.status === "accepted") return t("detail.accepted", { date: fmtDate(quote.accepted_at) });
  if (quote.status === "rejected") return t("detail.rejected");
  return t("detail.valid", { n: quote.valid_days });
};

export default function RequestDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const { t } = useT();
  const load = useCallback(() => api.get(`/requests/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Shell><p className="text-sm text-ink/50">{t("common.loading")}</p></Shell>;
  const { request: r, quote, events, payments, files } = d;

  const pay = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { request_id: r.id, kind, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const payPos = async (kind) => {
    setBusy(true);
    try { await api.post("/payments/pos", { request_id: r.id, kind }); toast.success(t("detail.posChosen")); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const cancelPos = async () => {
    setBusy(true);
    try { await api.post(`/payments/${r.pos_pending.tx_id}/cancel-pos`); toast.success(t("detail.posCancelled")); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const PayButtons = ({ kind, amount, testId }) => (
    <div className="mt-4 flex flex-wrap gap-3" data-testid={`pay-options-${kind}`}>
      <button data-testid={testId} className="btn-primary" disabled={busy} onClick={() => pay(kind)}><CreditCard size={16} /> {t("detail.payOnline")} {eur(amount)}</button>
      <button data-testid={`pay-pos-${kind}-button`} className="btn-secondary" disabled={busy} onClick={() => payPos(kind)}><Smartphone size={16} /> {t("detail.payPos")}</button>
    </div>
  );
  const decide = async (action) => {
    setBusy(true);
    try { await api.post(`/quotes/${quote.id}/${action}`, { reason: "" }); toast.success(action === "accept" ? t("detail.acceptedToast") : t("detail.rejectedToast")); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const balance = quote ? Math.max(quote.amount_due - (r.paid_total || 0), 0) : 0;
  const receipts = payments.filter((p) => p.payment_status === "paid");

  return (
    <Shell>
      <Link to="/app" className="btn-ghost -ml-3" data-testid="back-to-dashboard"><ArrowLeft size={16} /> {t("detail.back")}</Link>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4 rise">
        <div><p className="font-mono text-xs text-ink/50">{r.number} · {fmtDate(r.created_at)}</p><h1 className="mt-1 font-serif text-4xl sm:text-5xl font-bold">{r.service_name}</h1><p className="mt-2 text-ink/60">{r.address}, {r.city}</p></div>
        <StatusBadge status={r.status} testId="request-status-badge" />
      </div>
      <div className="mt-8 card-craft p-6 rise rise-1"><Steps steps={r.path === "inspection" ? t("steps.inspection") : t("steps.remote")} current={stepIndex(r)} /></div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {r.pos_pending && (
            <ActionCard title={t("detail.posTitle")} text={t("detail.posText", { amount: eur(r.pos_pending.amount), kind: t("detail.posKinds")[r.pos_pending.kind] })}>
              <div className="flex flex-wrap gap-3 items-center">
                <span className="inline-flex items-center gap-2 rounded-full bg-terracotta-light px-3 py-1 text-xs font-semibold text-terracotta" data-testid="pos-pending-badge"><Smartphone size={14} /> {t("detail.posBadge")}</span>
                <button data-testid="cancel-pos-button" className="btn-ghost text-xs" disabled={busy} onClick={cancelPos}>{t("detail.preferOnline")}</button>
              </div>
            </ActionCard>
          )}
          {r.status === "sopralluogo_da_pagare" && !r.pos_pending && (
            <ActionCard title={t("detail.payFeeTitle")} text={t("detail.payFeeText", { fee: eur(r.inspection_fee) })}>
              <PayButtons kind="inspection" amount={r.inspection_fee} testId="pay-inspection-button" />
            </ActionCard>
          )}
          {["sopralluogo_pagato", "sopralluogo_programmato"].includes(r.status) && (
            <ActionCard title={r.inspection_date ? t("detail.inspFixed") : t("detail.feeReceived")} text={r.inspection_date ? t("detail.techComing", { date: fmtDate(r.inspection_date, true) }) : t("detail.willContact")} />
          )}
          {r.status === "nuova" && <ActionCard title={t("detail.reviewing")} text={t("detail.reviewingText")} />}

          {quote && (
            <section className="card-craft p-6" data-testid="quote-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-serif text-2xl font-semibold">{t("detail.quote")} {quote.number}</h2>
                <span className="text-xs font-mono text-ink/50">{quoteStatusLabel(quote, t)}</span>
              </div>
              <div className="mt-5"><QuoteTable quote={quote} /></div>
              <div className="mt-2 flex flex-wrap gap-2 items-center"><a href={`${API}/quotes/${quote.id}/pdf`} target="_blank" rel="noreferrer" className="btn-secondary !py-1.5 !px-3 text-xs" data-testid="quote-pdf-link"><Download size={14} /> {t("detail.downloadPdf")}</a><ListenButton label={t("detail.listen")} text={`Preventivo ${quote.number} per ${r.service_name}. ${quote.items.map((i) => `${i.description}: ${i.qty} per ${eur(i.unit_price)}`).join(". ")}. Totale IVA inclusa ${eur(quote.total)}.${quote.inspection_credit ? ` Scomputo fee sopralluogo ${eur(quote.inspection_credit)}.` : ""} Importo dovuto ${eur(quote.amount_due)}. ${quote.deposit_pct >= 100 ? "Pagamento intero prima dell'inizio." : `Acconto del ${quote.deposit_pct} per cento, pari a ${eur(quote.deposit_amount)}, e saldo a fine lavori.`}${quote.notes ? ` Note: ${quote.notes}` : ""}`} /></div>
              {r.status === "preventivo_inviato" && (
                <div className="mt-6 flex flex-wrap gap-3">
                  <button data-testid="btn-accetta-preventivo" className="btn-primary" disabled={busy} onClick={() => decide("accept")}><Check size={16} /> {t("detail.accept")}</button>
                  <button data-testid="btn-rifiuta-preventivo" className="btn-secondary" disabled={busy} onClick={() => decide("reject")}><X size={16} /> {t("detail.reject")}</button>
                </div>
              )}
              {r.status === "preventivo_accettato" && !r.pos_pending && (
                <div className="mt-6 rounded-xl bg-linen border border-craft p-5">
                  <p className="text-sm">{quote.deposit_pct >= 100 ? t("detail.depositFull") : t("detail.depositPct", { pct: quote.deposit_pct })} {t("detail.onlineOrPos")}</p>
                  <PayButtons kind="deposit" amount={quote.deposit_amount} testId="pay-deposit-button" />
                </div>
              )}
              {r.status === "saldo_da_pagare" && !r.pos_pending && (
                <div className="mt-6 rounded-xl bg-linen border border-craft p-5">
                  <p className="text-sm">{t("detail.balanceText")}</p>
                  <PayButtons kind="balance" amount={balance} testId="pay-balance-button" />
                </div>
              )}
            </section>
          )}

          <section className="card-craft p-6">
            <h2 className="font-serif text-2xl font-semibold">{t("detail.progress")}</h2>
            <div className="mt-5"><EventFeed events={events} files={files} /></div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="card-craft p-6">
            <h3 className="font-semibold">{t("detail.intervention")}</h3>
            <dl className="mt-3 text-sm space-y-2 text-ink/70">
              <div className="flex justify-between"><dt>{t("detail.worker")}</dt><dd className="font-medium text-ink" data-testid="assigned-worker">{r.assigned_name || t("detail.toAssign")}</dd></div>
              <div className="flex justify-between"><dt>{t("detail.date")}</dt><dd className="font-mono" data-testid="scheduled-date">{fmtDate(r.scheduled_date, true)}</dd></div>
              <div className="flex justify-between"><dt>{t("detail.paid")}</dt><dd className="font-mono" data-testid="paid-total">{eur((r.paid_total || 0) + (r.inspection_paid ? r.inspection_fee : 0))}</dd></div>
            </dl>
          </section>
          <section className="card-craft p-6">
            <h3 className="font-semibold flex items-center gap-2"><FileText size={16} /> {t("detail.documents")}</h3>
            <ul className="mt-3 space-y-2 text-sm" data-testid="documents-list">
              {quote && quote.status !== "draft" && <li className="flex justify-between items-center gap-2"><span>{t("detail.quote")} {quote.number}</span><a href={`${API}/quotes/${quote.id}/pdf`} target="_blank" rel="noreferrer" className="text-terracotta inline-flex items-center gap-1 font-mono" data-testid="quote-pdf-doc-link">{eur(quote.amount_due)} <Download size={12} /></a></li>}
              {receipts.map((p) => (
                <li key={p.id} className="flex justify-between items-center gap-2"><span>{t("detail.receipt")} · {p.label}{p.method === "pos" ? " (POS)" : ""}</span>
                  <span className="inline-flex items-center gap-2"><a href={`${API}/payments/${p.id}/receipt-pdf`} target="_blank" rel="noreferrer" className="text-terracotta inline-flex items-center gap-1 font-mono" data-testid={`receipt-pdf-${p.kind}`}>{eur(p.amount)} <Download size={12} /></a>{p.receipt_url && <a href={p.receipt_url} target="_blank" rel="noreferrer" className="text-ink/40" data-testid={`receipt-link-${p.kind}`}><ExternalLink size={12} /></a>}</span>
                </li>
              ))}
              {!quote && receipts.length === 0 && <li className="text-ink/50">{t("detail.noDocs")}</li>}
            </ul>
          </section>
          {r.photo_ids?.length > 0 && (
            <section className="card-craft p-6"><h3 className="font-semibold">{t("detail.yourPhotos")}</h3><div className="mt-3 grid grid-cols-3 gap-2">{r.photo_ids.map((id) => <Photo key={id} id={id} className="h-20 w-full" />)}</div></section>
          )}
          {["nuova", "sopralluogo_da_pagare", "preventivo_inviato"].includes(r.status) && (
            <button data-testid="cancel-request-button" className="btn-ghost text-red-700 w-full" onClick={async () => { if (!window.confirm(t("detail.cancelConfirm"))) return; await api.post(`/requests/${r.id}/cancel`, { reason: "" }); load(); }}>{t("detail.cancel")}</button>
          )}
        </aside>
      </div>
    </Shell>
  );
}

function ActionCard({ title, text, children }) {
  return (
    <section className="card-craft p-6 border-l-4 !border-l-terracotta" data-testid="action-card">
      <h2 className="font-serif text-2xl font-semibold">{title}</h2><p className="mt-2 text-sm text-ink/65">{text}</p>
      {children && <div className="mt-4">{children}</div>}
    </section>
  );
}
